/**
 * Heuristics to distinguish real M11 protocol headings from numbered body sentences.
 * Used by Word export so the native TOC field does not ingest random lines.
 */

const PROSE_START =
  /^(of|in|at|within|during|these|each|per|the|a|an|for|to|from|with|and|or|if|when|where|all|any|approximately|about|up\s+to|more\s+than|less\s+than|after|before|on|by)\b/i;

function isTitleCaseBanner(title) {
  const words = String(title || '')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 2) return false;
  const titled = words.filter(
    (w) => /^[A-Z][a-z][\w'-]*$/.test(w) || /^[A-Z]{2,}$/.test(w) || /^\([^)]+\)$/.test(w),
  );
  return titled.length >= Math.max(2, Math.ceil(words.length * 0.55));
}

const INTERNAL_TOC_MARKERS =
  /^(FIELD_UPDATE_TRIGGER|TIMING_DELAY_\d+|PAGE_NUMBER_CALCULATION_TRIGGER)$/i;

/**
 * @returns {null | { number: string, title: string, kind: 'main' | 'subsection' }}
 */
export function parseProtocolHeadingLine(line, { sectionRootPrefix = null } = {}) {
  const trimmed = String(line ?? '').trim();
  if (!trimmed || INTERNAL_TOC_MARKERS.test(trimmed)) return null;

  let match = trimmed.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
  if (match) {
    const [, number, title] = match;
    if (!isLikelyProtocolHeading(number, title, { sectionRootPrefix, kind: 'subsection' })) return null;
    return { number, title: title.trim(), kind: 'subsection' };
  }

  match = trimmed.match(/^(\d+)\.?\s+(.+)$/);
  if (match) {
    const [, number, title] = match;
    if (!isLikelyProtocolHeading(number, title, { sectionRootPrefix, kind: 'main' })) return null;
    return { number, title: title.trim(), kind: 'main' };
  }

  return null;
}

export function isLikelyProtocolHeading(number, title, opts = {}) {
  const { sectionRootPrefix = null, kind = 'subsection', maxTitleLength = kind === 'subsection' ? 140 : 100 } = opts;
  const t = String(title ?? '').trim();
  const num = String(number ?? '').trim();

  if (!num || !t || t === '.' || /^\.+$/.test(t)) return false;
  if (INTERNAL_TOC_MARKERS.test(t)) return false;
  if (t.length > maxTitleLength) return false;

  if (PROSE_START.test(t) && !isTitleCaseBanner(t)) return false;

  const words = t.split(/\s+/).filter(Boolean);
  if (words.length > (kind === 'subsection' ? 24 : 16)) return false;

  // Long sentences with punctuation are body text, not headings.
  if ((t.match(/[.!?]/g) || []).length > 1) return false;
  if (t.includes(',') && t.length > 80) return false;

  const objectiveLabel = t.match(/^((?:Primary|Secondary|Exploratory)\s+Objective)\s*:?\s*(.*)$/i);
  if (objectiveLabel) {
    const label = objectiveLabel[1].trim();
    const rest = (objectiveLabel[2] || '').trim();
    if (!rest || rest.length < 8) return true;
    if (/\b(to evaluate|to assess|to determine|to compare|to characterize)\b/i.test(rest)) return false;
    return label.split(/\s+/).length <= 4;
  }

  if (t.includes(':')) {
    const [beforeColon, afterColon] = t.split(/:\s*/, 2);
    if (beforeColon && afterColon) {
      const labelWords = beforeColon.trim().split(/\s+/).length;
      if (labelWords <= 10 && isTitleCaseBanner(beforeColon.trim())) {
        if (/\b(to evaluate|to assess|to determine|to compare|will|shall)\b/i.test(afterColon)) return false;
        if (afterColon.length > 20) return false;
      }
    }
  }

  if (
    kind === 'subsection' &&
    /\b(will|shall|should|must|may|can)\b/i.test(t) &&
    t.length > 55
  ) {
    return false;
  }
  if (
    kind === 'subsection' &&
    /\b(participants?|patients?)\b/i.test(t) &&
    /\b(will|shall|who have|who are|that have|that are)\b/i.test(t) &&
    t.length > 55
  ) {
    return false;
  }
  if (
    kind !== 'subsection' &&
    /\b(participants?|patients?|treatment|dose|cycle|weeks?|months?|days?|mg|will|shall|should|must|may|can)\b/i.test(t) &&
    t.length > 35
  ) {
    return false;
  }

  const parts = num.split('.');
  if (parts.some((p) => p === '')) return false;

  if (kind === 'subsection') {
    if (sectionRootPrefix) {
      const ok =
        num === sectionRootPrefix ||
        num.startsWith(`${sectionRootPrefix}.`) ||
        // Allow sibling sections embedded in long combined exports (e.g. next main section).
        (parts.length === 1 && /^\d+$/.test(num));
      if (!ok && parts.length > 1) {
        const rootParts = String(sectionRootPrefix).split('.');
        const lineRoot = parts.slice(0, rootParts.length).join('.');
        if (lineRoot !== sectionRootPrefix && !num.startsWith(`${sectionRootPrefix}.`)) {
          return false;
        }
      }
    }
  }

  if (kind === 'main') {
    // Main headings are typically ALL CAPS or Title Case banners, not prose.
    const upperWords = words.filter((w) => /^[A-Z][A-Z0-9/&-]*$/.test(w) || /^[A-Z]/.test(w)).length;
    const looksLikeBanner = upperWords >= Math.max(2, Math.ceil(words.length * 0.6));
    const allCaps = t === t.toUpperCase() && /[A-Z]/.test(t);
    if (!looksLikeBanner && !allCaps && words.length > 4) return false;
    if (/\d+\s*(weeks?|months?|days?|hours?|mg|ml|%)/i.test(t)) return false;
  }

  return true;
}

export function shouldSkipDuplicateSectionHeading(lineNumber, sectionTitle) {
  const main = (String(sectionTitle || '').match(/^(\d+)/) || [])[1];
  if (!main || !lineNumber) return false;
  return String(lineNumber) === main;
}
